const BASE_URL = `${window.location.origin}/api`;
const REQUEST_TIMEOUT_MS = 10_000;

type ApiErrorResponse = {
  error: string;
  code: number;
};

export type StatusResponse = {
  uplink: {
    interface_name: string;
    status: string;
    rx_rate: number;
    tx_rate: number;
    last_update: string;
  } | null;
  netwatch: Array<{
    id: number;
    name: string;
    host: string;
    status: string;
    since: string;
    last_update: string;
  }>;
  queues: Array<{
    id: number;
    name: string;
    target: string;
    rate_download: number;
    rate_upload: number;
    max_limit_download: number;
    max_limit_upload: number;
    last_update: string;
  }>;
  speedtest: {
    mikrotik: {
      download: number;
      upload: number;
      ping: number;
      server_name: string;
      recorded_at: string;
    } | null;
    ookla: {
      download: number;
      upload: number;
      ping: number;
      server_name: string;
      recorded_at: string;
    } | null;
  };
  summary: {
    clients_up: number;
    clients_down: number;
    total_download: number;
    total_upload: number;
    uplink_status: "up" | "down";
  };
};

export type InterfaceHistoryResponse = {
  range: string;
  resolution: string;
  data: Array<{
    timestamp: string;
    rx_rate: number;
    tx_rate: number;
  }>;
};

export type ClientHistoryResponse = {
  client: string;
  range: string;
  resolution: string;
  data: Array<{
    timestamp: string;
    rate_download: number;
    rate_upload: number;
  }>;
};

export type SpeedtestHistoryResponse = {
  range: string;
  data: {
    mikrotik: Array<{
      timestamp: string;
      download: number;
      upload: number;
      ping: number;
      server_name: string;
    }>;
    ookla: Array<{
      timestamp: string;
      download: number;
      upload: number;
      ping: number;
      server_name: string;
    }>;
  };
};

export type ClientsResponse = {
  clients: Array<{
    name: string;
    target: string;
  }>;
};

async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  try {
    const response = await fetch(`${BASE_URL}${path}`, {
      ...options,
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        ...(options.headers ?? {}),
      },
    });

    window.clearTimeout(timeoutId);

    const data = (await response.json()) as T | ApiErrorResponse;

    if (!response.ok) {
      const errorResponse = data as ApiErrorResponse;
      throw new Error(errorResponse.error || `HTTP ${response.status}`);
    }

    return data as T;
  } catch (error) {
    window.clearTimeout(timeoutId);

    if (error instanceof Error && error.name === "AbortError") {
      throw new Error("Request timeout");
    }

    throw error;
  }
}

export function fetchStatus(): Promise<StatusResponse> {
  return apiFetch<StatusResponse>("/status");
}

export function fetchInterfaceHistory(range: string): Promise<InterfaceHistoryResponse> {
  return apiFetch<InterfaceHistoryResponse>(`/history/interface?range=${encodeURIComponent(range)}`);
}

export function fetchClientHistory(name: string, range: string): Promise<ClientHistoryResponse> {
  const query = new URLSearchParams({ name, range });
  return apiFetch<ClientHistoryResponse>(`/history/client?${query.toString()}`);
}

export function fetchSpeedtestHistory(range: string): Promise<SpeedtestHistoryResponse> {
  return apiFetch<SpeedtestHistoryResponse>(`/history/speedtest?range=${encodeURIComponent(range)}`);
}

export function fetchClients(): Promise<ClientsResponse> {
  return apiFetch<ClientsResponse>("/clients");
}

export type HardwareInterface = {
  name: string;
  status: string;
};

export type HardwareWireless = {
  tx_signal: number;
  rx_signal: number;
  recorded_at: string;
};

export type HardwareDevice = {
  name: string;
  type: string;
  connection_status: "up" | "down";
  cpu_load: number;
  memory_used: number;
  memory_total: number;
  memory_percent: number;
  last_update: string;
  interfaces: HardwareInterface[];
  wireless: HardwareWireless | null;
};

export type HardwareResponse = {
  devices: HardwareDevice[];
};

export type ResourceHistoryPoint = {
  timestamp: string;
  cpu_load: number;
  memory_percent: number;
};

export type WirelessHistoryPoint = {
  timestamp: string;
  tx_signal: number;
  rx_signal: number;
};

export type HardwareHistoryResponse = {
  device: string;
  range: string;
  resource_history: ResourceHistoryPoint[];
  wireless_history: WirelessHistoryPoint[];
};

export function fetchHardware(): Promise<HardwareResponse> {
  return apiFetch<HardwareResponse>("/hardware");
}

export function fetchHardwareHistory(
  device: string,
  range: string
): Promise<HardwareHistoryResponse> {
  const query = new URLSearchParams({ device, range });
  return apiFetch<HardwareHistoryResponse>(`/history/hardware?${query.toString()}`);
}
